<script>
import axios from '@/utils/axios';

const DEFAULT_FORM = () => ({
  id: null,
  name: '',
  code: '',
  type: 'earning',
  calculation_type: 'fixed',
  value: 0,
  is_statutory: false,
  is_taxable: false,
  description: '',
});

export default {
  inject: ['setLoading'],
  emits: ['updated'],
  data() {
    return {
      form: DEFAULT_FORM(),
      errors: {},
      deleteItem: null,
      types: [
        { value: 'earning', label: 'Earning' },
        { value: 'deduction', label: 'Deduction' },
      ],
      calcTypes: [
        { value: 'fixed', label: 'Fixed Amount' },
        { value: 'percentage_of_basic', label: 'Percentage of Basic' },
        { value: 'percentage_of_gross', label: 'Percentage of Gross' },
        { value: 'percentage_of_ctc', label: 'Percentage of CTC' },
      ],
    };
  },
  computed: {
    isPercentage() {
      return this.form.calculation_type !== 'fixed';
    },
  },
  methods: {
    add() {
      this.resetForm();
    },

    edit(item) {
      this.resetForm();
      this.form.id = item.id;
      this.form.name = item.name || '';
      this.form.code = item.code || '';
      this.form.type = item.type || 'earning';
      this.form.calculation_type = item.calculation_type || 'fixed';
      this.form.value = item.value != null ? parseFloat(item.value) : 0;
      this.form.is_statutory = !!item.is_statutory;
      this.form.is_taxable = !!item.is_taxable;
      this.form.description = item.description || '';
    },

    setDelete(item) {
      this.deleteItem = item;
    },

    buildPayload() {
      return {
        name: this.form.name,
        code: this.form.code,
        type: this.form.type,
        calculation_type: this.form.calculation_type,
        value: this.form.value,
        is_statutory: this.form.is_statutory,
        is_taxable: this.form.is_taxable,
        description: this.form.description || null,
      };
    },

    async save() {
      this.errors = {};
      this.setLoading(true);
      try {
        let response;
        if (this.form.id) {
          response = await axios.put(`/hrms/salary-components/${this.form.id}`, this.buildPayload());
        } else {
          response = await axios.post('/hrms/salary-components', this.buildPayload());
        }
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('hrm_salary_component_modal');
        this.resetForm();
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async deleteComponent() {
      this.setLoading(true);
      try {
        const response = await axios.delete(`/hrms/salary-components/${this.deleteItem.id}`);
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('hrm_salary_component_delete_modal');
      } catch (error) {
        if (error.type === 'validation') {
          this.$swalToast(error.errors?.id?.[0] || error.message, 'error');
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    resetForm() {
      this.form = DEFAULT_FORM();
      this.errors = {};
    },
  },
};
</script>

<template>
  <!-- Add/Edit Modal -->
  <div class="modal fade" id="hrm_salary_component_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ form.id ? 'Edit' : 'Add' }} Salary Component</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="save">
          <div class="modal-body pb-0">
            <div class="row">

              <!-- Name -->
              <div class="col-md-7 mb-3">
                <label class="form-label">Name <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="form.name" placeholder="e.g. House Rent Allowance" />
                <div v-if="errors.name" class="text-danger small mt-1">{{ errors.name[0] }}</div>
              </div>

              <!-- Code -->
              <div class="col-md-5 mb-3">
                <label class="form-label">Code <span class="text-danger">*</span></label>
                <input type="text" class="form-control text-uppercase" v-model="form.code" placeholder="e.g. HRA" />
                <div v-if="errors.code" class="text-danger small mt-1">{{ errors.code[0] }}</div>
              </div>

              <!-- Type -->
              <div class="col-md-4 mb-3">
                <label class="form-label">Type <span class="text-danger">*</span></label>
                <select class="form-select" v-model="form.type">
                  <option v-for="t in types" :key="t.value" :value="t.value">{{ t.label }}</option>
                </select>
                <div v-if="errors.type" class="text-danger small mt-1">{{ errors.type[0] }}</div>
              </div>

              <!-- Calculation Type -->
              <div class="col-md-4 mb-3">
                <label class="form-label">Calculation Type <span class="text-danger">*</span></label>
                <select class="form-select" v-model="form.calculation_type">
                  <option v-for="c in calcTypes" :key="c.value" :value="c.value">{{ c.label }}</option>
                </select>
                <div v-if="errors.calculation_type" class="text-danger small mt-1">{{ errors.calculation_type[0] }}</div>
              </div>

              <!-- Value -->
              <div class="col-md-4 mb-3">
                <label class="form-label">
                  {{ isPercentage ? 'Percentage (%)' : 'Amount' }} <span class="text-danger">*</span>
                </label>
                <input type="number" min="0" step="0.01" class="form-control" v-model.number="form.value" />
                <div v-if="errors.value" class="text-danger small mt-1">{{ errors.value[0] }}</div>
              </div>

              <!-- Is Statutory -->
              <div class="col-md-6 mb-3">
                <label class="form-label d-block">Statutory?</label>
                <div class="form-check form-switch mt-2">
                  <input class="form-check-input" type="checkbox" role="switch" v-model="form.is_statutory" id="scStatutory" />
                  <label class="form-check-label ms-1" for="scStatutory">{{ form.is_statutory ? 'Yes' : 'No' }}</label>
                </div>
              </div>

              <!-- Is Taxable -->
              <div class="col-md-6 mb-3">
                <label class="form-label d-block">Taxable?</label>
                <div class="form-check form-switch mt-2">
                  <input class="form-check-input" type="checkbox" role="switch" v-model="form.is_taxable" id="scTaxable" />
                  <label class="form-check-label ms-1" for="scTaxable">{{ form.is_taxable ? 'Yes' : 'No' }}</label>
                </div>
              </div>

              <!-- Description -->
              <div class="col-12 mb-3">
                <label class="form-label">Description</label>
                <textarea class="form-control" rows="2" v-model="form.description" placeholder="Enter description (optional)"></textarea>
                <div v-if="errors.description" class="text-danger small mt-1">{{ errors.description[0] }}</div>
              </div>

            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary">{{ form.id ? 'Update' : 'Save' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade" id="hrm_salary_component_delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">You want to delete this salary component? This can't be undone.</p>
          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
            <a href="javascript:void(0);" class="btn btn-danger" @click="deleteComponent">Yes, Delete</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>